import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import {SpeciesTypeService} from '../../../../../services/speciesType.service';
import SpeciesTypeModel from '../../../../../services/speciesType.model';
import {QpRangeService} from '../../../../../services/qpRange.service';
import QpRangesModel from '../../../../../services/qpRange.model';
import {QueryParamsService} from '../../../../../services/queryParams.service';
import QueryParamsModel from '../../../../../services/queryParams.model';
import {CultureCategoryService} from '../../../../../services/cultureCategory.service';
import CultureCategoryModel from '../../../../../services/cultureCategory.model';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";
import { MatSelectChange } from '@angular/material/select';


/**
 * QpRangesComponent
 *
 * This component is responsible for managing Quality Parameter (QP) Ranges within the Test Configurations module.
 * It provides a user interface for administrators to view, create, update, and search for QP ranges.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all QP ranges.
 * - Provide search functionality to filter ranges by QP name, culture category, or species type.
 * - Manage the creation of new QP ranges with validation.
 * - Handle updates to existing QP ranges.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update QP ranges.
 * - **Real-time Search**: Filter list by multiple attributes.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures numeric inputs for min/max ranges and required selections.
 * - **Dynamic Dropdowns**: Loads species types based on selected culture category.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all QP ranges from `QpRangeService`.
 * 3. Fetches reference data (QPs, Culture Categories) for dropdowns.
 * 4. Initializes `BatchLoader` with the full dataset.
 * 5. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 6. `addQPRange` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-qp-ranges
 * @example
 * <app-qp-ranges></app-qp-ranges>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-qp-ranges',
  templateUrl: './qp-ranges.component.html',
  styleUrls: ['./qp-ranges.component.scss']
})
export class QpRangesComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched QP ranges from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {QpRangesModel[]}
   * @default []
   */
  public qpRanges: QpRangesModel[] = [];

  /**
   * Array holding the currently displayed QP ranges.
   * This list is populated by the BatchLoader (subset of qpRanges or filteredQpRanges).
   * @type {QpRangesModel[]}
   * @default []
   */
  public qpRangesList: QpRangesModel[] = [];

  /**
   * Model for the current QP range being added or edited.
   * Bound to the form for data manipulation.
   * @type {QpRangesModel}
   */
  public qpRange: QpRangesModel = new QpRangesModel();

  /**
   * Array holding QP ranges that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {QpRangesModel[]}
   * @default []
   */
  public filteredQpRanges: QpRangesModel[] = [];

  /**
   * Array of available Quality Parameters for selection.
   * @type {Array<QueryParamsModel>}
   */
  public queryParams: Array<QueryParamsModel>;

  /**
   * Array of available Culture Categories for selection.
   * @type {Array<CultureCategoryModel>}
   */
  public cultureCategories: Array<CultureCategoryModel>;

  /**
   * Array of available Species Types for selection.
   * @type {Array<SpeciesTypeModel>}
   */
  public speciesTypes: Array<SpeciesTypeModel>;

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Flag for min/max validation (not actively used in current logic).
   * @type {boolean}
   */
  public minMaxvalidation: boolean;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public QpRangesDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Reactive form group for QP range details.
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for QpRangesComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - qp_id: Required.
   * - culture_category_id: Required.
   * - species_type_id: Required.
   * - min_acc_range: Required, Numeric pattern.
   * - max_acc_range: Required, Numeric pattern.
   *
   * @constructor
   * @param {ToastrService} toastrService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {QpRangeService} qpRangeService - Service for QP Range API operations.
   * @service QpRangeService
   * @param {QueryParamsService} queryParamsService - Service for Query Params API operations.
   * @service QueryParamsService
   * @param {CultureCategoryService} cultureCategoryService - Service for Culture Category API operations.
   * @service CultureCategoryService
   * @param {SpeciesTypeService} speciesTypeService - Service for Species Type API operations.
   * @service SpeciesTypeService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toastrService: ToastrService, public formBuilder: FormBuilder, public sidenavaction: Sidenavaction, public qpRangeService: QpRangeService, public queryParamsService: QueryParamsService, public cultureCategoryService: CultureCategoryService, public speciesTypeService: SpeciesTypeService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.qpRange = new QpRangesModel();
    this.registerForm = this.formBuilder.group({
      qp_id: ['', [Validators.required]],
      culture_category_id: ['', [Validators.required]],
      species_type_id: ['', [Validators.required]],
      min_acc_range: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
      max_acc_range: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all QP ranges from the backend.
   * 5. Fetches reference data (QPs, Culture Categories).
   * 6. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.sidenavaction.x_navigation();
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.qpRanges = [];
    this.qpRangesList = [];
    this.filteredQpRanges = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      await this.getQpRanges();
      this.queryParams = await this.queryParamsService.getAllQueryParam();
      this.cultureCategories = await this.cultureCategoryService.getAllCultureCategories();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method f
   * @returns {AbstractControl} Form controls
   */
  get f() {
    return this.registerForm.controls;
  }

  /**
   * Fetches all QP ranges from the backend.
   * Initializes the batch loader.
   *
   * @async
   * @method getQpRanges
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async getQpRanges() {
    try {
      this.qpRanges = await this.qpRangeService.getAllQpRanges();
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredQpRanges : this.qpRanges,
      (updatedList) => {
        this.qpRangesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests for all injected services.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.qpRangeService.cancelOnGoingHttpRequests();
    this.queryParamsService.cancelOnGoingHttpRequests();
    this.cultureCategoryService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  /**
   * Fetches species types based on the selected culture category ID.
   * Triggered when culture category selection changes.
   *
   * @async
   * @method getSpeciesTypeByCultureCategoryId
   * @param {MatSelectChange} event - The selection change event.
   * @returns {Promise<void>}
   */
  async getSpeciesTypeByCultureCategoryId(event: MatSelectChange) {
    const cultureCategoryId = event.value;
    try {
      this.speciesTypes = await this.speciesTypeService.getSpeciesTypeByCultureCategoryId(cultureCategoryId);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Filters the QP range list based on the search query.
   *
   * - Filters by QP name, culture category name, or species type name (case-insensitive).
   * - Updates `filteredQpRanges`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredQpRanges`, `qpRangesList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.qpRanges.filter(route =>
        route.qp.name?.toLowerCase().includes(query) ||
        route.cultureCategory.name?.toLowerCase().includes(query) ||
        route.speciesType.name?.toLowerCase().includes(query)
      )
      : this.qpRanges;
    this.filteredQpRanges = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.qpRangesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.qpRange = new QpRangesModel();
    this.QpRangesDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  /**
   * Handles the submission of the QP Range form (Add or Update).
   *
   * - Validates the form.
   * - Checks for duplicate ranges (QP + Culture Category + Species Type).
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addQPRange
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addQPRange() {
    this.submitted = true;
    // Stop if invalid
    if (this.registerForm.invalid) return;
    const formData = this.registerForm.value;
    try {
      this.QpRangesDoubleClick = true;
      if (this.submitButton) {
        // Extract IDs
        const qp = formData.qp_id;
        const cc = formData.culture_category_id;
        const st = formData.species_type_id;
        // Check for duplicates
        const existing = this.qpRanges.find(o =>
          o.qp_id === qp &&
          o.culture_category_id === cc &&
          o.species_type_id === st
        );
        if (existing) {
          this.toastrService.warning('Range already available');
          this.registerForm.patchValue({
            qp_id: '',
            culture_category_id: '',
            species_type_id: '',
            min_acc_range: '',
            max_acc_range: ''
          });
          return;
        }
        await this.qpRangeService.create(formData);
        this.toastrService.success('QP range created successfully', '', { timeOut: 2000 });
        this.minMaxvalidation = false;
        await this.ngOnInit();
      } else {
        const updateQpRange = { ...formData };
        delete updateQpRange.id;
        await this.qpRangeService.updateOneByID(this.qpRange.id, updateQpRange);
        this.toastrService.success('QP range updated successfully', '', { timeOut: 2000 });
        this.qpRange = new QpRangesModel();
        await this.ngOnInit();
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.QpRangesDoubleClick = false;
    }
  }


  /**
   * Retrieves a QP Range by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getQPRangeById
   * @param {string} id - The ID of the QP range to edit.
   * @returns {Promise<void>}
   */
  public async getQPRangeById(id: string) {
    this.submitButton = false;
    this.showForm = true;
    this.updateButton = true;
    this.QpRangesDoubleClick = false;
    this.speciesTypes = await this.speciesTypeService.getAllSpeciesType();
    const foundQpRange = this.qpRangesList.find(uom => uom?.id === id);
    this.qpRange = foundQpRange ? { ...foundQpRange } : new QpRangesModel(); // clone object
    if (this.qpRange) {
      this.registerForm.patchValue({
        qp_id: this.qpRange.qp_id,
        culture_category_id: this.qpRange.culture_category_id,
        species_type_id: this.qpRange.species_type_id,
        min_acc_range: this.qpRange.min_acc_range,
        max_acc_range: this.qpRange.max_acc_range
      });
    }
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
